import type { JunctionConfig } from '../config/level.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { ConnectorRole, type ConnectorDefinition } from './connectors/ConnectorDefinition';
import { forwardOf, rightOf } from './connectors/Pose';
import type { SegmentInstance } from './segments/SegmentInstance';
import type { TrackManager } from './TrackManager';

export type TurnDirection = 'LEFT' | 'RIGHT';
export type TurnOption = TurnDirection | 'STRAIGHT';
export type TurnOutcome = 'NONE' | 'TURNED' | 'STRAIGHT' | 'MISSED';

/** The part of the player the turn system needs (keeps it testable without the controller). */
export interface TurnablePlayer {
  readonly current: { readonly distance: number; readonly x: number };
  applyTurn(distance: number, lateral: number): void;
}

export interface TurnHint {
  readonly options: readonly TurnOption[];
  readonly decided: TurnDirection | null;
  /** No straight option – missing the turn is a mistake. */
  readonly forced: boolean;
  readonly distanceToPivot: number;
}

/**
 * The runner's 90° turn seen from its corner (see `TurnController.bend`) – what the
 * presentation needs to let the body run a smooth bend instead of turning on the spot.
 */
export interface TurnBend {
  /** World heading of the approach (before the turn). */
  readonly approachYaw: number;
  /** −1: left turn, +1: right turn. */
  readonly side: -1 | 1;
  /** Track meters past the corner (negative: still on the approach). */
  readonly progress: number;
  /** Track meters between the turn decision and the corner (0: decided at or after it – a late turn). */
  readonly leadIn: number;
}

interface UpcomingJunction {
  readonly segment: SegmentInstance;
  readonly pivotDistance: number;
}

interface Decision {
  readonly segmentUid: number;
  readonly exit: ConnectorDefinition;
  /**
   * Exit lane (lateral offset) a late decision turns onto – null: the runner's own
   * lateral offset (decided in time; follows lane changes until the corner).
   */
  readonly lane: number | null;
  /** Meters to the corner when the decision was made (0: turn on the spot). */
  readonly leadIn: number;
  /** The runner has been before its corner since the decision – it is crossed during a step, not passed already. */
  approaching: boolean;
}

/** The last executed turn – the corner the body bends around. */
interface ExecutedTurn {
  /** Track distance of the corner on the exit leg. */
  readonly cornerDistance: number;
  readonly approachYaw: number;
  readonly side: -1 | 1;
  readonly leadIn: number;
}

/**
 * Re-expresses a track position (lateral x, path parameter s) of a junction
 * in the frame of the chosen exit leg. The world position stays identical,
 * so the turn is visually continuous for every lane.
 */
export function remapToExit(segment: SegmentInstance, exit: ConnectorDefinition, x: number, s: number): { s: number; x: number } {
  const pivot = segment.definition.junction?.pivot;
  if (!pivot) return { s, x };
  const dx = x - pivot.x;
  const dz = s - pivot.z;
  const f = forwardOf(exit.transform.yaw);
  const r = rightOf(exit.transform.yaw);
  return { s: pivot.z + dx * f.x + dz * f.z, x: dx * r.x + dz * r.z };
}

/**
 * Path parameter (segment-local) where a runner at lateral x reaches the corner of a
 * turn: its lane line meets the same lane of the exit leg (pivot ± x).
 */
export function cornerOf(segment: SegmentInstance, exit: ConnectorDefinition, x: number): number {
  const pivot = segment.definition.junction?.pivot;
  if (!pivot) return segment.definition.length;
  return pivot.z + (exit.role === ConnectorRole.Left ? x - pivot.x : pivot.x - x);
}

/**
 * 90° turns at junction modules.
 *
 * - On the junction square itself – `decisionWindow` meters before the pivot plus the touch latency at the
 *   current speed (decisionWindowAt) – left/right input becomes a turn decision (if that exit exists) instead
 *   of a lane change. Before it a press is a lane change and nothing is kept for the junction (user
 *   2026-10-03: turning must be exact).
 * - The turn executes where the player's lane crosses the exit lane (pivot ± x):
 *   lanes are preserved. A decided turn takes the corner exactly – the part of the
 *   step past the corner continues along the exit – so the lateral offset (and a
 *   lane change in progress) carries over unchanged.
 * - Decided only after the own corner was passed: the runner turns onto the next exit
 *   lane it reaches (each exit lane line runs across the junction square), landing
 *   exactly in that lane – no sideways correction against the running direction.
 * - Past the last exit lane line (or auto-turned after a missed turn): the turn happens
 *   on the spot, the world position does not jump and the runner settles into the
 *   nearest lane.
 * - No decision by pivot + `lateTurnGrace`: crossings continue straight,
 *   turns and T-junctions count as a missed turn (RunDirector decides the cost).
 */
export class TurnController {
  private decision: Decision | null = null;
  private executed: ExecutedTurn | null = null;

  constructor(
    private readonly track: TrackManager,
    private readonly config: JunctionConfig,
    private readonly events: EventBus<GameEvents>,
  ) {}

  reset(): void {
    this.decision = null;
    this.executed = null;
  }

  /**
   * Called for left/right input at the runner's track position (distance, lateral x) and run speed (m/s).
   * Returns true if the input was used as a turn decision.
   */
  tryDecide(direction: TurnDirection, distance: number, x: number, speed: number): boolean {
    const junction = this.upcoming(distance);
    if (!junction || distance < junction.pivotDistance - this.decisionWindowAt(speed)) return false;
    if (this.decision?.segmentUid === junction.segment.uid) return false; // already decided → lane change
    const { segment } = junction;
    const exit = segment.definition.exits.find((e) => e.role === direction);
    if (!exit) return false;
    const s = segment.localZ(distance);
    const own = cornerOf(segment, exit, x);
    const lane = own >= s ? null : nextExitLane(segment, exit, s);
    const corner = lane === null ? own : cornerOf(segment, exit, lane);
    const leadIn = Math.max(0, corner - s);
    this.decision = { segmentUid: segment.uid, exit, lane, leadIn, approaching: corner >= s };
    this.events.emit('turn:decided', { direction });
    return true;
  }

  update(player: TurnablePlayer): TurnOutcome {
    const junction = this.upcoming(player.current.distance);
    if (!junction) return 'NONE';
    const { segment } = junction;
    const pivotZ = segment.definition.junction?.pivot.z ?? 0;
    const s = segment.localZ(player.current.distance);

    const decision = this.decision;
    if (decision?.segmentUid === segment.uid) {
      const corner = cornerOf(segment, decision.exit, decision.lane ?? player.current.x);
      if (s >= corner) {
        // Crossed the corner during this step: take it exactly. Otherwise (decided past every lane line): on the spot.
        this.execute(segment, decision.exit, player, decision.approaching ? corner : null, decision.leadIn);
        return 'TURNED';
      }
      decision.approaching = true;
    }

    if (s >= pivotZ + this.config.lateTurnGrace) {
      const straight = segment.definition.exits.find((e) => e.role === ConnectorRole.Out);
      if (straight) {
        this.resolve(segment, straight);
        return 'STRAIGHT';
      }
      return 'MISSED';
    }
    return 'NONE';
  }

  /** Rescue after a missed turn (extra life spent): take the exit on the player's side. */
  autoTurn(player: TurnablePlayer): void {
    const junction = this.upcoming(player.current.distance);
    if (!junction) return;
    const exits = junction.segment.definition.exits.filter((e) => e.role !== ConnectorRole.Out);
    const preferred = player.current.x < 0 ? ConnectorRole.Left : ConnectorRole.Right;
    const exit = exits.find((e) => e.role === preferred) ?? exits[0];
    if (exit) this.execute(junction.segment, exit, player, null, 0);
  }

  /** Information about the junction ahead for camera and drink break (null before the approach window). */
  hint(distance: number): TurnHint | null {
    const junction = this.upcoming(distance);
    if (!junction || distance < junction.pivotDistance - this.config.approachWindow) return null;
    const exits = junction.segment.definition.exits;
    const decided = this.decision?.segmentUid === junction.segment.uid ? this.decision.exit.role : null;
    return {
      options: exits.map((e) => (e.role === ConnectorRole.Out ? 'STRAIGHT' : (e.role as TurnDirection))),
      decided: decided === ConnectorRole.Left || decided === ConnectorRole.Right ? decided : null,
      forced: !exits.some((e) => e.role === ConnectorRole.Out),
      distanceToPivot: junction.pivotDistance - distance,
    };
  }

  /**
   * The decided turn ahead (progress < 0) or the last executed one (progress ≥ 0) relative
   * to the runner at (distance, x) – null when no turn is decided or taken yet. Presentation
   * only (the body's bend); the caller decides how far around the corner it matters.
   */
  bend(distance: number, x: number): TurnBend | null {
    const junction = this.upcoming(distance);
    const decision = this.decision;
    if (junction && decision?.segmentUid === junction.segment.uid) {
      const { segment } = junction;
      // Not past the corner before the turn executes (a decision past it executes in the same step).
      const progress = Math.min(0, segment.localZ(distance) - cornerOf(segment, decision.exit, decision.lane ?? x));
      return { approachYaw: segment.pose.yaw, side: sideOf(decision.exit), progress, leadIn: decision.leadIn };
    }
    const executed = this.executed;
    if (!executed) return null;
    return { approachYaw: executed.approachYaw, side: executed.side, progress: distance - executed.cornerDistance, leadIn: executed.leadIn };
  }

  /** Meters before the pivot from which a press turns: the junction square plus the touch latency at this speed. */
  decisionWindowAt(speed: number): number {
    return this.config.decisionWindow + Math.max(0, speed) * this.config.decisionLatencySeconds;
  }

  /** Next unresolved junction on the committed path. */
  private upcoming(distance: number): UpcomingJunction | null {
    for (const segment of this.track.segments) {
      if (!segment.isJunction || segment.isResolved || segment.endDistance < distance) continue;
      return { segment, pivotDistance: segment.pivotDistance ?? segment.endDistance };
    }
    return null;
  }

  /** `corner`: path parameter of the corner the runner just crossed, or null to turn on the spot. */
  private execute(segment: SegmentInstance, exit: ConnectorDefinition, player: TurnablePlayer, corner: number | null, leadIn: number): void {
    const s = segment.localZ(player.current.distance);
    const at = corner ?? s;
    const mapped = remapToExit(segment, exit, player.current.x, at);
    this.resolve(segment, exit);
    const cornerDistance = segment.startDistance + mapped.s;
    this.executed = { cornerDistance, approachYaw: segment.pose.yaw, side: sideOf(exit), leadIn: corner === null ? 0 : leadIn };
    player.applyTurn(cornerDistance + (s - at), mapped.x);
    this.events.emit('turn:executed', { direction: exit.role === ConnectorRole.Left ? 'LEFT' : 'RIGHT' });
  }

  private resolve(segment: SegmentInstance, exit: ConnectorDefinition): void {
    if (this.track.pendingBranch?.junction === segment) this.track.commitBranch(exit);
    segment.resolve(exit);
    this.decision = null;
  }
}

/** Lateral offset of the first exit lane whose line (across the junction square) lies at or ahead of s – null if none. */
function nextExitLane(segment: SegmentInstance, exit: ConnectorDefinition, s: number): number | null {
  let best: number | null = null;
  for (const lane of segment.definition.lanes) {
    const corner = cornerOf(segment, exit, lane.centerX);
    if (corner >= s && (best === null || corner < cornerOf(segment, exit, best))) best = lane.centerX;
  }
  return best;
}

function sideOf(exit: ConnectorDefinition): -1 | 1 {
  return exit.role === ConnectorRole.Left ? -1 : 1;
}
